const PUBLIC_TOKEN_PREFIXES = [
  '/s/',
  '/t/',
  '/sign/documents/',
  '/pay/',
  '/w/',
  '/schedule/shared/',
  '/installations/sign/',
  '/service-agreements/sign/',
  '/venue-intake/',
  '/q/',
] as const;

const PUBLIC_AUTH_PATHS = new Set([
  '/login',
  '/auth/callback',
  '/reset-password',
  '/accept-invite',
]);

export const SESSION_RETURN_PATH_KEY = 'ita.auth.returnPath.v1';

export function isPublicTokenPath(pathname: string): boolean {
  return PUBLIC_TOKEN_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function isClientPortalPublicPath(pathname: string): boolean {
  // These routes expose only unauthenticated recovery copy. Their event,
  // dashboard, and invoice transports remain session-only; see
  // isAnonymousTransportPath and ClientPortalRouteGate.
  if (pathname === '/client-portal') return true;
  if (!pathname.startsWith('/client-portal/')) return false;
  return !pathname.startsWith('/client-portal/service/');
}

/** Routes that may intentionally use anonymous/public data transport. */
export function isAnonymousTransportPath(pathname: string): boolean {
  return PUBLIC_AUTH_PATHS.has(pathname) || isPublicTokenPath(pathname);
}

export function isPublicAppPath(pathname: string): boolean {
  return isAnonymousTransportPath(pathname)
    || isClientPortalPublicPath(pathname);
}

export function isProtectedAppPath(pathname: string): boolean {
  return !isPublicAppPath(pathname);
}

/** Accept only a same-origin SPA path, never an absolute or protocol-relative URL. */
export function validateReturnPath(value: string | null | undefined): string | null {
  const candidate = value?.trim();
  if (!candidate || !candidate.startsWith('/') || candidate.startsWith('//')) return null;
  try {
    const parsed = new URL(candidate, 'https://return-path.invalid');
    if (parsed.origin !== 'https://return-path.invalid') return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function currentRelativePath(location: {
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  return validateReturnPath(
    `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`,
  ) ?? '/';
}

export function readStoredReturnPath(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return validateReturnPath(window.sessionStorage.getItem(SESSION_RETURN_PATH_KEY));
  } catch {
    return null;
  }
}

export function storeReturnPath(path: string): void {
  if (typeof window === 'undefined') return;
  const safePath = validateReturnPath(path);
  if (!safePath) return;
  try {
    window.sessionStorage.setItem(SESSION_RETURN_PATH_KEY, safePath);
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }
}

export function consumeStoredReturnPath(): string | null {
  const path = readStoredReturnPath();
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(SESSION_RETURN_PATH_KEY);
    } catch {
      // Best effort only.
    }
  }
  return path;
}
